import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Eye, Users as UsersIcon, Timer, TrendingUp, Download } from "lucide-react";
import { Link } from "react-router-dom";
import CompensationTrend from "@/components/CompensationTrend";

export default function FacilityStats() {
    const [stats, setStats] = useState(null);

    useEffect(() => {
        api.get("/facility/stats").then(({ data }) => setStats(data.data));
    }, []);

    const download = async (path, filename) => {
        const res = await api.get(path, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = filename; document.body.appendChild(a); a.click();
        a.remove(); URL.revokeObjectURL(url);
    };

    if (!stats) return <div className="text-sm text-[#5C5C5C]">Caricamento…</div>;

    const kpis = [
        { label: "Viste totali", val: stats.total_views, icon: Eye, testId: "kpi-views" },
        { label: "Media viste/annuncio", val: stats.avg_views_per_job, icon: TrendingUp, testId: "kpi-avg-views" },
        { label: "Tempo medio risposta", val: stats.avg_response_hours != null ? `${stats.avg_response_hours}h` : "—", icon: Timer, testId: "kpi-response" },
        { label: "Annunci totali", val: stats.total_jobs, icon: UsersIcon, testId: "kpi-total-jobs" },
    ];

    return (
        <div className="max-w-6xl mx-auto space-y-6">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Statistiche</h1>
                    <p className="text-sm text-[#5C5C5C] mt-1">Ottimizza gli annunci con i dati.</p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm"
                        onClick={() => download("/export/applications.csv", "candidature.csv")}
                        data-testid="export-apps-csv">
                        <Download className="w-4 h-4 mr-2" />Candidature CSV
                    </Button>
                    <Button variant="outline" size="sm"
                        onClick={() => download("/export/stats.csv", "statistiche.csv")}
                        data-testid="export-stats-csv">
                        <Download className="w-4 h-4 mr-2" />Statistiche CSV
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {kpis.map((k) => (
                    <div key={k.label} className="hl-card p-5" data-testid={k.testId}>
                        <k.icon className="w-4 h-4 text-[#1C3F39]" />
                        <div className="mt-3 font-display text-2xl font-bold font-mono text-[#1A1A1A]">{k.val ?? 0}</div>
                        <div className="text-xs text-[#5C5C5C] mt-1">{k.label}</div>
                    </div>
                ))}
            </div>

            <CompensationTrend />

            <div className="hl-card p-6">
                <h2 className="font-display font-bold text-lg mb-4">Performance per annuncio</h2>
                <div className="divide-y divide-[#E5E5E0]">
                    <div className="grid grid-cols-6 gap-2 py-2 text-xs font-mono text-[#5C5C5C] uppercase tracking-wide">
                        <div className="col-span-2">Titolo</div>
                        <div>Data</div>
                        <div>Stato</div>
                        <div className="text-right">Viste</div>
                        <div className="text-right">Candidature</div>
                    </div>
                    {stats.per_job.length === 0 && <div className="py-8 text-center text-sm text-[#5C5C5C]">Nessun annuncio ancora.</div>}
                    {stats.per_job.map((j) => (
                        <Link to={`/app/job/${j.job_id}`} key={j.job_id}
                            className="grid grid-cols-6 gap-2 py-3 items-center hover:bg-[#F9F9F7] transition-colors"
                            data-testid={`stat-row-${j.job_id}`}>
                            <div className="col-span-2 font-medium text-sm truncate">{j.title}</div>
                            <div className="text-xs text-[#5C5C5C]">{j.date}</div>
                            <div><Badge variant="outline" className="text-xs">{j.status}</Badge></div>
                            <div className="text-right font-mono text-sm">{j.views}</div>
                            <div className="text-right font-mono text-sm">{j.applications}</div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
